import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LOOP_EXPLAINER,
  NIGHTLY_WORKFLOW,
  NIGHTLY_YML_PATH,
  listAgents,
  parsePlanner,
  parseTasks,
  toggleEnabledLine,
  type AgentPrompt,
  type AgentTask,
  type PlannerBlock,
} from "../lib/agents";
import {
  dispatchWorkflow,
  latestWorkflowRun,
  readFile,
  writeFile,
  type RepoConfig,
  type WorkflowRun,
} from "../lib/github";

/**
 * Agents panel — the steering surface.
 *
 * Reads: `.github/agents/*.agent.md` (roster + one-line purpose),
 * `agentic/dev/agents/nightly.yml` (which agents run, enabled flags), the planner
 * block, and the latest workflow run.
 * Writes: exactly one line per toggle (`enabled:` in nightly.yml).
 * Actions: dispatch the nightly workflow with `agents` + `dry_run` inputs.
 */
export function Agents({ config, onBack }: { config: RepoConfig; onBack: () => void }) {
  const [tasks, setTasks] = useState<AgentTask[] | null>(null);
  const [agents, setAgents] = useState<AgentPrompt[]>([]);
  const [yamlText, setYamlText] = useState<string | null>(null);
  const [yamlSha, setYamlSha] = useState<string | undefined>(undefined);
  const [planner, setPlanner] = useState<PlannerBlock | null>(null);
  const [run, setRun] = useState<WorkflowRun | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dryRun, setDryRun] = useState(true);
  const [armed, setArmed] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const file = await readFile(config, NIGHTLY_YML_PATH);
      setYamlText(file.content);
      setYamlSha(file.sha);
      setTasks(parseTasks(file.content));
      setPlanner(parsePlanner(file.content));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read nightly.yml");
    }
    try {
      setAgents(await listAgents(config));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read the agent roster");
    }
    try {
      setRunError(null);
      setRun(await latestWorkflowRun(config, NIGHTLY_WORKFLOW));
    } catch (e) {
      setRunError(e instanceof Error ? e.message : "Run status unavailable");
    }
  }, [config]);

  useEffect(() => {
    void load();
  }, [load]);

  const taskByAgent = useMemo(() => {
    const map = new Map<string, AgentTask>();
    for (const t of tasks ?? []) map.set(t.agent, t);
    return map;
  }, [tasks]);

  /** Flip `enabled:` for one agent — a targeted one-line edit in nightly.yml. */
  async function toggle(agent: string, enabled: boolean) {
    if (!yamlText) return;
    const next = toggleEnabledLine(yamlText, agent, enabled);
    if (next === null) {
      setError(`Agent \`${agent}\` is not configured in nightly.yml`);
      return;
    }
    setBusy(agent);
    setError(null);
    setNotice(null);
    try {
      const sha = await writeFile(
        config,
        NIGHTLY_YML_PATH,
        next,
        `${enabled ? "Enable" : "Disable"} ${agent} agent (console)`,
        yamlSha,
      );
      setYamlText(next);
      setYamlSha(sha);
      setTasks(parseTasks(next));
      setNotice(`${agent} ${enabled ? "enabled" : "disabled"} — takes effect on the next run.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Write failed");
    } finally {
      setBusy(null);
    }
  }

  /** Dispatch the nightly workflow. Dry-run is the default; real runs need a confirm. */
  async function runNow(which: string, agentsInput: string) {
    if (!dryRun && armed !== which) {
      setArmed(which);
      setNotice("Real run armed — press the same button again to confirm.");
      return;
    }
    setArmed(null);
    setBusy(`run-${which}`);
    setError(null);
    setNotice(null);
    try {
      await dispatchWorkflow(config, NIGHTLY_WORKFLOW, {
        agents: agentsInput,
        dry_run: dryRun ? "true" : "false",
      });
      setNotice(
        dryRun
          ? "Dry run dispatched — it reports what would run, without spending tokens."
          : "Run dispatched — status refreshes below.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Dispatch failed");
    } finally {
      setBusy(null);
    }
  }

  function toggleSelect(agent: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(agent)) next.delete(agent);
      else next.add(agent);
      return next;
    });
  }

  const enabledCount = (tasks ?? []).filter((t) => t.enabled).length;

  return (
    <div className="mx-auto max-w-2xl pb-24">
      <header className="sticky top-0 z-10 border-b border-ink-800 bg-ink-950/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="-ml-1 rounded-lg p-1.5 text-ink-400 hover:bg-ink-850 hover:text-ink-100"
            aria-label="Back"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="flex-1 text-base font-semibold text-ink-100">Agents</h1>
          <button onClick={() => void load()} className="chip bg-ink-850 text-ink-400">
            refresh
          </button>
        </div>
      </header>

      <div className="space-y-3 p-4">
        {error && (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-xl border border-accent/40 bg-accent/10 p-3 text-sm text-accent-soft">
            {notice}
          </div>
        )}

        {/* Latest run */}
        <section className="card p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-200">Last run</h2>
            {runError ? (
              <span className="text-xs text-danger">{runError}</span>
            ) : run ? (
              <span className="text-xs text-ink-500">
                #{run.runNumber} · {timeAgo(run.createdAt)} · {run.event}
              </span>
            ) : (
              <span className="text-xs text-ink-500">no run yet</span>
            )}
          </div>
          {run && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className={`chip ${runStatusStyle(run)}`}>{run.conclusion ?? run.status}</span>
              <a
                className="text-xs text-accent-soft underline decoration-dotted"
                href={run.htmlUrl}
                target="_blank"
                rel="noreferrer"
              >
                open log
              </a>
            </div>
          )}
        </section>

        {/* Steer */}
        <section className="card space-y-3 p-4">
          <h2 className="text-sm font-semibold text-ink-200">Steer</h2>
          <p className="text-xs leading-relaxed text-ink-500">
            {enabledCount} of {tasks?.length ?? "?"} agents run on the next nightly run.
            Dispatch now to run them immediately (the workflow runs the same configuration).
          </p>
          <label className="flex items-center gap-2 text-sm text-ink-300">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => {
                setDryRun(e.target.checked);
                setArmed(null);
              }}
              className="h-4 w-4 accent-current"
            />
            Dry run first (report what would happen — costs no tokens)
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              className="btn-primary"
              disabled={busy !== null}
              onClick={() => void runNow("all", "")}
            >
              {busy === "run-all"
                ? "Dispatching…"
                : armed === "all"
                  ? "Confirm real run (all)"
                  : dryRun
                    ? "Dry-run all"
                    : "Run all"}
            </button>
            <button
              className="btn-ghost"
              disabled={busy !== null || selected.size === 0}
              onClick={() => void runNow("selected", [...selected].join(","))}
            >
              {busy === "run-selected"
                ? "Dispatching…"
                : armed === "selected"
                  ? `Confirm real run (${selected.size})`
                  : dryRun
                    ? `Dry-run selected (${selected.size})`
                    : `Run selected (${selected.size})`}
            </button>
          </div>
          {!dryRun && (
            <p className="text-xs text-warn">
              Real runs spend tokens. A full run is 8 agents with a tool loop each.
            </p>
          )}
        </section>

        {/* Agent cards */}
        <section className="space-y-2">
          {agents.map((a) => {
            const task = taskByAgent.get(a.name);
            const configured = task !== undefined;
            const enabled = task?.enabled ?? false;
            return (
              <div key={a.name} className={`card p-4 ${configured ? "" : "opacity-60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-medium text-ink-100">{a.name}</span>
                      {task?.domain && <span className="chip bg-ink-850 text-ink-500">{task.domain}</span>}
                      {task?.domains && (
                        <span className="chip bg-ink-850 text-ink-500" title={task.domains}>
                          {domainSummary(task.domains)}
                        </span>
                      )}
                      <span className={`chip ${enabled ? "bg-accent/20 text-accent-soft" : "bg-ink-850 text-ink-500"}`}>
                        {configured ? (enabled ? "enabled" : "disabled") : "not configured"}
                      </span>
                    </div>
                    {a.description && (
                      <p className="mt-1.5 text-xs leading-relaxed text-ink-400">{a.description}</p>
                    )}
                  </div>
                  <button
                    role="switch"
                    aria-checked={enabled}
                    disabled={!configured || busy !== null}
                    onClick={() => void toggle(a.name, !enabled)}
                    className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-40 ${
                      enabled ? "bg-accent" : "bg-ink-700"
                    }`}
                    title={enabled ? "Disable this agent" : "Enable this agent"}
                  >
                    <span
                      className="absolute top-0.5 h-5 w-5 rounded-full bg-ink-950 transition-all"
                      style={{ left: enabled ? 22 : 2 }}
                    />
                  </button>
                </div>
                {configured && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-ink-500">
                      <input
                        type="checkbox"
                        checked={selected.has(a.name)}
                        onChange={() => toggleSelect(a.name)}
                        className="h-3.5 w-3.5"
                      />
                      select
                    </label>
                    <button
                      className="text-xs text-accent-soft underline decoration-dotted disabled:opacity-40"
                      disabled={busy !== null}
                      onClick={() => void runNow(a.name, a.name)}
                    >
                      {armed === a.name ? "confirm run" : dryRun ? "dry-run this" : "run this"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Planner + explain */}
        <section className="card space-y-2 p-4">
          <h2 className="text-sm font-semibold text-ink-200">Implementation planner</h2>
          <p className="text-xs leading-relaxed text-ink-400">
            {planner
              ? `Runs as a step of the nightly workflow — up to ${planner.maxItems} item(s) marked `
              : "Runs as a step of the nightly workflow — items marked "}
            <code className="text-ink-300">next:plan</code> get a plan file next to them, then wait for
            you as <code className="text-ink-300">next:human</code>. Plans only — never code.
          </p>
        </section>

        <section className="card space-y-2 p-4">
          <h2 className="text-sm font-semibold text-ink-200">How the loop works</h2>
          {LOOP_EXPLAINER.map((line) => (
            <p key={line} className="text-xs leading-relaxed text-ink-400">
              {line}
            </p>
          ))}
        </section>
      </div>
    </div>
  );
}

function runStatusStyle(run: WorkflowRun): string {
  if (run.status !== "completed") return "bg-warn/15 text-warn";
  if (run.conclusion === "success") return "bg-accent/20 text-accent-soft";
  if (run.conclusion === "failure") return "bg-danger/15 text-danger";
  return "bg-ink-850 text-ink-400";
}

/** Short label for a `domains:` list — "all domains" / "3 domains" / the raw value. */
function domainSummary(raw: string): string {
  if (/^all\b/i.test(raw)) return "all domains";
  if (raw.startsWith("[")) {
    const n = raw.slice(1, -1).split(",").filter((s) => s.trim()).length;
    return n === 1 ? "1 domain" : `${n} domains`;
  }
  return raw;
}

/** "3h" / "2d" — coarse age of a timestamp. */
function timeAgo(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}
